import React, { useEffect, useState } from 'react';
import { paymentsAPI, clientsAPI, authAPI } from '../services/api';
import { ModernCard, ModernTable, ModernLoadingSpinner, ModernSearchBar, ModernSelect, ModernButton, ModernInput } from '../components/ui/ModernComponents';
import { Download, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PaiementsHistorique = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [clients, setClients] = useState([]);
  const [stores, setStores] = useState([]);
  const [filters, setFilters] = useState({ search: '', client_id: '', type: '', mode: '', start_date: '', end_date: '', store_id: '' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await paymentsAPI.exportPaymentsJson();
        setPayments(res.data);
        setFiltered(res.data);
        const clientsRes = await clientsAPI.getClients();
        setClients(clientsRes.data);
      } catch {
        setPayments([]);
        setFiltered([]);
        setClients([]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      authAPI.getStores().then(res => setStores(res.data)).catch(() => setStores([]));
    }
  }, [user]);

  // Filtering logic
  useEffect(() => {
    let data = [...payments];
    if (filters.search) {
      data = data.filter(p => (p.note || '').toLowerCase().includes(filters.search.toLowerCase()));
    }
    if (filters.client_id) {
      data = data.filter(p => String(p.client_id) === String(filters.client_id));
    }
    if (filters.type) {
      data = data.filter(p => p.reference_type === filters.type);
    }
    if (filters.mode) {
      data = data.filter(p => p.mode === filters.mode);
    }
    if (filters.start_date) {
      data = data.filter(p => p.payment_date && p.payment_date >= filters.start_date);
    }
    if (filters.end_date) {
      data = data.filter(p => p.payment_date && p.payment_date <= filters.end_date);
    }
    if (filters.store_id) {
      data = data.filter(p => String(p.store_id) === String(filters.store_id));
    }
    setFiltered(data);
  }, [filters, payments]);

  // Export to CSV
  const handleExport = () => {
    const csv = [
      'ID,Client,Type,Reference,Montant,Mode,Date,Note',
      ...filtered.map(p => [
        p.id,
        p.client_name || '-',
        p.reference_type_label || p.reference_type,
        p.reference_id,
        p.amount,
        p.mode,
        p.payment_date,
        p.note ? '"' + p.note.replace(/"/g, '""') + '"' : ''
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'paiements_historique.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const columns = [
    { key: 'id', label: 'ID' },
    ...(user?.role === 'admin' ? [{ key: 'store_name', label: 'Magasin' }] : []),
    { key: 'client_name', label: 'Client' },
    { key: 'reference_type_label', label: 'Type' },
    { key: 'reference_id', label: 'Référence' },
    { key: 'amount', label: 'Montant', render: v => <span className="font-semibold text-primary-700">{Number(v).toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' })}</span> },
    { key: 'mode', label: 'Mode' },
    { key: 'payment_date', label: 'Date', render: v => v ? <span className="text-neutral-500">{v.slice(0, 10)}</span> : '-' },
    { key: 'note', label: 'Note', render: v => <span className="text-neutral-400">{v}</span> },
  ];

  return (
    <div className="space-y-8 p-6">
      <h1 className="text-2xl font-bold mb-4">Historique des paiements</h1>
      <div className="flex justify-end mb-4">
        <ModernButton variant="ghost" icon={<Filter className="w-5 h-5" />} onClick={() => setShowFilters(f => !f)}>
          Filtres
        </ModernButton>
      </div>
      {showFilters && (
        <ModernCard className="p-6 mb-6 shadow-lg rounded-xl bg-white border border-neutral-200 animate-fade-in-up">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <ModernSearchBar placeholder="Recherche note..." onSearch={search => setFilters(f => ({ ...f, search }))} className="rounded-lg border border-neutral-200 px-3 py-2" />
              <ModernSelect label="Client" value={filters.client_id} onChange={e => setFilters(f => ({ ...f, client_id: e.target.value }))} options={[{ value: '', label: 'Tous' }, ...clients.map(c => ({ value: c.id, label: c.name }))]} className="rounded-lg border border-neutral-200 px-3 py-2" />
              <ModernSelect label="Type" value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} options={[{ value: '', label: 'Tous' }, { value: 'sales_order', label: 'Commande' }, { value: 'sale', label: 'Vente directe' }, { value: 'sales_invoice', label: 'Facture' }]} className="rounded-lg border border-neutral-200 px-3 py-2" />
              <ModernSelect label="Mode" value={filters.mode} onChange={e => setFilters(f => ({ ...f, mode: e.target.value }))} options={[{ value: '', label: 'Tous' }, { value: 'espece', label: 'Espèce' }, { value: 'cheque', label: 'Chèque' }, { value: 'carte_bancaire', label: 'Carte bancaire' }]} className="rounded-lg border border-neutral-200 px-3 py-2" />
              {user?.role === 'admin' && (
                <ModernSelect label="Magasin" value={filters.store_id} onChange={e => setFilters(f => ({ ...f, store_id: e.target.value }))} options={[{ value: '', label: 'Tous' }, ...stores.map(s => ({ value: s.id, label: s.name }))]} className="rounded-lg border border-neutral-200 px-3 py-2" />
              )}
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-2">
              <div className="flex gap-2 w-full md:w-auto">
                <ModernInput type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} className="rounded-lg border border-neutral-200 px-3 py-2 w-full" />
                <ModernInput type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} className="rounded-lg border border-neutral-200 px-3 py-2 w-full" />
              </div>
              <div className="flex justify-end w-full md:w-auto">
                <ModernButton variant="primary" icon={<Download className="w-4 h-4" />} onClick={handleExport}>Exporter CSV</ModernButton>
              </div>
            </div>
          </div>
        </ModernCard>
      )}
      <ModernCard className="p-6 shadow-lg rounded-xl bg-white border border-neutral-200">
        {loading ? (
          <ModernLoadingSpinner size="lg" />
        ) : (
          <ModernTable columns={columns} data={filtered} className="w-full table-zebra table-striped" />
        )}
      </ModernCard>
    </div>
  );
};

export default PaiementsHistorique; 